/**
 * SeoMeasurePage — `/seo/measure` (B3). Замер органика→MQL и ДВА CPL.
 * Прямой CPL = Σ фактических затрат ÷ MQL зрелой когорты. Ввод затрат по статьям.
 */

import { useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { HelpButton } from '@/components/layout/HelpButton';
import { SEO_HELP } from '@/help/seoHelp';
import { Button, LoadingBlock, ErrorBlock, Badge, IconButton } from '@/shared/ui';
import { useSeoMeasure, useSeoCosts, useSeoMutations } from '@/hooks/api/useSeo';

function rub(v: number | null | undefined): string {
  if (v == null) return '—';
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(v);
}
const CATS = ['staff', 'content', 'dev', 'tools', 'other'];

export function SeoMeasurePage() {
  usePageHeader({ title: 'SEO — Органика → MQL', subtitle: 'ROI в дисциплине рекламы (CPL ≤ 15 000 ₽)', actions: <HelpButton help={SEO_HELP.measure} /> });
  const m = useSeoMeasure();
  const costs = useSeoCosts();
  const { createCost, deleteCost } = useSeoMutations();

  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const [month, setMonth] = useState(defaultMonth);
  const [category, setCategory] = useState('staff');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  const addCost = () => {
    const a = Number(amount);
    if (!month || !Number.isFinite(a) || a <= 0) return;
    createCost.mutate({ period_month: month, category, amount: a, note: note.trim() || undefined }, {
      onSuccess: () => { setAmount(''); setNote(''); },
    });
  };

  return (
    <div className="col gap20" style={{ maxWidth: 1050 }}>
      {m.isLoading && <LoadingBlock label="Считаю замер…" />}
      {m.isError && <ErrorBlock text={m.error?.message} onRetry={() => void m.refetch()} />}

      {m.data && (
        <>
          {!m.data.metrikaConfigured && (
            <div className="card" style={{ borderLeft: '3px solid var(--warning, #CA8A04)' }}>
              <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
                <Badge tone="b-warning">Metrika не настроена</Badge>{' '}
                Нет YANDEX_METRIKA_TOKEN — органические сессии/MQL недоступны.
              </p>
            </div>
          )}
          {m.data.attribution === 'partial' && (
            <div className="card" style={{ borderLeft: '3px solid var(--warning, #CA8A04)' }}>
              <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
                <Badge tone="b-warning">атрибуция частичная</Badge>{' '}
                Связь форма→сделка неполная, пока не внедрён проброс UTM в CRM (docs/utm-passthrough-tz.md).
                {!m.data.callsConfigured && ' Коллтрекинг не подключён — звонковые MQL не учтены.'}
                {m.data.bitrixOrganicDeals != null && ` Кросс-чек Bitrix: сделок с utm_medium=organic — ${m.data.bitrixOrganicDeals}.`}
              </p>
            </div>
          )}

          <section className="stat-grid">
            <Stat label="Органические MQL (форма + звонок)" value={String(m.data.mqlTotal)} sub={`форм ${m.data.mqlForms} · зв ${m.data.callsConfigured ? m.data.mqlCalls : '—'}`} />
            <Stat label="Прямой органический CPL" value={rub(m.data.directCpl)} sub={m.data.directCplNote ?? `лимит ${rub(m.data.cplCap)}`} />
            <Stat label="Сессии по ПС" value={`${m.data.sessions.yandex}/${m.data.sessions.google}`} sub="Яндекс / Google" />
            <Stat label="Зрелых / зреющих страниц" value={`${m.data.maturePages} / ${m.data.maturingPages}`} sub="когорта для прямого CPL" />
          </section>

          <section className="card">
            <h3 style={{ marginTop: 0 }}>Затраты SEO (для прямого CPL)</h3>
            <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
              <input className="input" type="date" value={month} onChange={(e) => setMonth(e.target.value)} style={{ maxWidth: 160 }} />
              <select className="input" value={category} onChange={(e) => setCategory(e.target.value)} style={{ maxWidth: 140 }}>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input className="input" type="number" placeholder="сумма ₽" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ maxWidth: 140 }} />
              <input className="input" placeholder="комментарий" value={note} onChange={(e) => setNote(e.target.value)} style={{ minWidth: 160, flex: 1 }} />
              <Button variant="primary" size="sm" onClick={addCost} disabled={createCost.isPending || !amount}>Добавить</Button>
            </div>
            {createCost.isError && <ErrorBlock text={createCost.error?.message} />}

            {costs.data && costs.data.costs.length > 0 ? (
              <table className="tbl">
                <thead>
                  <tr><th>Месяц</th><th>Статья</th><th style={{ textAlign: 'right' }}>Сумма</th><th>Комментарий</th><th>Автор</th><th></th></tr>
                </thead>
                <tbody>
                  {costs.data.costs.map((c) => (
                    <tr key={c.id}>
                      <td className="mono" style={{ fontSize: 12 }}>{String(c.period_month).slice(0, 7)}</td>
                      <td><Badge tone="b-neutral">{c.category}</Badge></td>
                      <td className="tnum" style={{ textAlign: 'right' }}>{rub(Number(c.amount))}</td>
                      <td style={{ fontSize: 12.5 }}>{c.note ?? ''}</td>
                      <td className="muted mono" style={{ fontSize: 11 }}>@{c.created_by}</td>
                      <td style={{ textAlign: 'right' }}><IconButton icon="Trash" tip="Удалить" small danger onClick={() => deleteCost.mutate(c.id)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>Затрат ещё нет. Введите факты — итог месяца пойдёт в знаменатель прямого CPL.</p>
            )}
          </section>

          {m.data.byPage.length > 0 && (
            <section className="card">
              <h3 style={{ marginTop: 0 }}>MQL по страницам</h3>
              <table className="tbl">
                <thead><tr><th>Страница</th><th style={{ textAlign: 'right' }}>MQL</th></tr></thead>
                <tbody>
                  {m.data.byPage.map((p, i) => (
                    <tr key={i}><td className="mono" style={{ fontSize: 12 }}>{p.url}</td><td className="tnum" style={{ textAlign: 'right' }}>{p.mql}</td></tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value" style={{ fontSize: 24 }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
